import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validateNumber } from 'ember-changeset-validations/validators';
import { BufferedChangeset } from 'ember-changeset/types';
import DS from 'ember-data';
import IntlService from 'ember-intl/services/intl';
import BufferProfile from 'volta/models/buffer-profile';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { parseNumber } from 'volta/utils/math-utils';
import { isThenable } from 'volta/utils/object-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { scheduleOnce } from "@ember/runloop";

const FACTOR_RANGES = {
  min: 0,
  max: 1
};

const UpdateBufferProfileValidations = {
  bufferProfile: [],
  leadTimeFactor: validateNumber({ positive: true, allowBlank: true }),
  varFactor: validateNumber({ positive: true, allowBlank: true }),
  fixedRedBase: validateNumber({ positive: true, allowBlank: true }),
  fixedRedSafety: validateNumber({ positive: true, allowBlank: true }),
  fixedYellow: validateNumber({ positive: true, allowBlank: true }),
  fixedGreen: validateNumber({ positive: true, allowBlank: true })
};

export interface IBufferSettingsSubmit {
  /* Overridden lead-time factor*/
  leadTimeFactor: number;
  /* Overridden variability factor */
  varFactor: number;
  /* Buffer profile id assigned to the SKU */
  bufferProfileId: string;
  /* Optional Fixed red base value */
  fixedRedBase?: number;
  /* Optional Fixed red safety value */
  fixedRedSafety?: number;
  /* Optional Fixed yellow value */
  fixedYellow?: number;
  /* Optional Fixed green value */
  fixedGreen?: number;
}

export interface IBufferZones {
  redBase?: number;
  redSafety?: number;
  yellow?: number;
  green?: number;
}

interface IArgs extends IBufferSettingsSubmit {
  /* Full Buffer profiles list (TODO: It should be encapsulated in a <BufferProfilesSelect /> component, with lazy loading and auto pagination)*/
  bufferProfileOptions: BufferProfile[] | DS.PromiseManyArray<BufferProfile>;
  suggestedFixedBufferZones?: IBufferZones;
  /* Optional callback executed when submitting */
  onSubmit?: (data: any) => void;
  onInsert?: () => void;
  disabled?: boolean;
}

export default class BufferProfileEditor extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare intl: IntlService;

  // Properties
  // ~~~~~

  @tracked declare settings: IBufferSettingsSubmit;
  @tracked declare changeset: BufferedChangeset;

  factorRange = FACTOR_RANGES;

  // Computed Properties
  // ~~~~~

  get selectedBufferProfile() {
    return this.changeset?.bufferProfileId
      ? (this.args.bufferProfileOptions ?? []).findBy('id', this.changeset.get('bufferProfileId'))
      : undefined;
  }

  get hasFixedBufferZones() {
    const { changeset } = this;

    return (
      changeset &&
      (isPresent(changeset.get('fixedRedBase')) ||
        isPresent(changeset.get('fixedRedSafety')) ||
        isPresent(changeset.get('fixedYellow')) ||
        isPresent(changeset.get('fixedGreen')))
    );
  }

  // Actions
  // ~~~~~

  @action
  handleDidInsert() {
    this.args.onInsert?.();
    this.initChangeset();
    this.initValues(undefined);
  }

  @action
  handleDidUpdate() {
    this.initChangeset();
    scheduleOnce('actions', this, () => {
      this.safeInitValues(this.selectedBufferProfile);
    });
  }

  @action
  handleSubmit() {
    const cs = this.changeset;
    cs.validate();
    if (cs.isValid) {
      cs.execute();
      this.args.onSubmit?.(this.getDataToSubmit());
    } else {
      defaultErrorHandler(cs.errors as any);
    }
  }

  @action
  onLeadTimeFactorChange(value: number) {
    this.changeset.set('leadTimeFactor', value);
    this.changeset.set('bufferProfileId', null);
  }

  @action
  onVarFactorChange(value: number) {
    this.changeset.set('varFactor', value);
    this.changeset.set('bufferProfileId', null);
  }

  @action
  onBufferProfileChange(selectedBufferProfile: BufferProfile) {
    this.changeset.set('bufferProfileId', selectedBufferProfile.id);
    this.changeset.set('leadTimeFactor', selectedBufferProfile.leadTimeFactor);
    this.changeset.set('varFactor', selectedBufferProfile.varFactor);
    this.handleSubmit();
  }

  @action
  onFixedBufferZoneChange(key: string, value: string | number) {
    this.changeset.set(key, parseNumber(value));
    this.handleSubmit();
  }

  // Helper functions
  // ~~~~~

  initChangeset() {
    const {
      leadTimeFactor,
      varFactor,
      bufferProfileId,
      fixedRedBase,
      fixedRedSafety,
      fixedYellow,
      fixedGreen
    } = this.args;
    const settings = {
      leadTimeFactor: isPresent(leadTimeFactor) ? leadTimeFactor : 1,
      varFactor: isPresent(varFactor) ? varFactor : 1,
      bufferProfileId,
      fixedRedBase,
      fixedRedSafety,
      fixedYellow,
      fixedGreen
    };

    this.settings = settings;
    const cs = Changeset(
      this.settings,
      lookupValidator(UpdateBufferProfileValidations),
      UpdateBufferProfileValidations
    );
    this.changeset = cs;
  }

  initValues(bp?: BufferProfile) {
    const bpId = bp ? bp.id : undefined;
    const {
      leadTimeFactor,
      varFactor,
      bufferProfileId,
      fixedRedBase,
      fixedRedSafety,
      fixedYellow,
      fixedGreen
    } = this.args;
    this.changeset.set('bufferProfileId', bpId ? bpId : bufferProfileId);
    this.changeset.set(
      'leadTimeFactor',
      bpId ? (bp as BufferProfile).leadTimeFactor : isPresent(leadTimeFactor) ? leadTimeFactor : 1.0
    );
    this.changeset.set(
      'varFactor',
      bpId ? (bp as BufferProfile).varFactor : isPresent(varFactor) ? varFactor : 1.0
    );

    this.changeset.set('fixedRedBase', fixedRedBase);
    this.changeset.set('fixedRedSafety', fixedRedSafety);
    this.changeset.set('fixedYellow', fixedYellow);
    this.changeset.set('fixedGreen', fixedGreen);
  }

  safeInitValues(bufferProfile?: BufferProfile | Promise<BufferProfile>) {
    if (!bufferProfile) {
      return;
    }

    if (isThenable(bufferProfile as Promise<BufferProfile>)) {
      (bufferProfile as Promise<BufferProfile>).then((bp: BufferProfile) => {
        this.initValues(bp);
      });
    } else {
      this.initValues(bufferProfile as BufferProfile);
    }
  }

  getDataToSubmit(): IBufferSettingsSubmit {
    return {
      ...(this.settings ?? {})
    };
  }
}
